import {HttpClient} from '@angular/common/http';
import {Injectable, signal} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {APIResponse, Pageable} from '@core/helpers/types';
import {ConfigService} from '@core/config/config.service';
import {FolderCreateDto} from '../types/folder-create.dto';
import {FolderInviteDto} from '../types/folder-invite.dto';
import {FolderViewDto} from '../types/folder-view.dto';
import {FolderEditDto} from '../types/folder-edit.dto';
import {FolderMemberEditDto} from '../types/folder-member-edit.dto';
import {FolderMemberViewDto} from '../types/folder-member-view.dto';
import {FolderFilterDto} from '../types/folder-filter.dto';
import {clean} from '@core/helpers/fn';
import {FolderDetails} from '../types/folder-details';
import {TranslateService} from '@core/modules/translation';
import {AuthService} from '@core/modules/auth';
import {FolderActions} from '../store/folder.actions';
import {Store} from '@ngrx/store';
import {AppState} from 'src/app/store/app.state';

@Injectable({providedIn: 'root'})
export class FoldersService {
	private get ROOT_URL() {
		return this.configService.getApiUrl() + '/folders';
	}
	private readonly FOLDER_MAX_LEVEL_ALLOWED = 2;

	constructor(
		private readonly http: HttpClient,
		private readonly translator: TranslateService,
		private readonly authenticator: AuthService,
		private readonly configService: ConfigService,
		private store: Store<AppState>
	) {}

	// Get account's folder or specific one
	getFolders = (dto: FolderFilterDto): Observable<Pageable<FolderViewDto>> => {
		const params = clean({...dto});
		if (dto?.folder_uuid) params.folder_uuid = dto.folder_uuid;

		return this.http
			.get<APIResponse<FolderViewDto[]>>(this.ROOT_URL, {
				params,
			})
			.pipe(map(res => ({data: res.data ?? [], pagination: res.pagination})));
	};

	getFoldersFlat = (
		account_uuid: string,
		folder_uuid?: string
	): Observable<FolderViewDto[]> =>
		this.getFolders({account_uuid, folder_uuid}).pipe(
			map(folders => {
				const flattenedFolders: FolderViewDto[] = [];
				function flatten(folder: FolderViewDto) {
					flattenedFolders.push(folder);
					if (folder.subFolders) {
						folder.subFolders.forEach(flatten);
					}
				}
				folders.data.forEach(flatten);
				return flattenedFolders;
			})
		);

	// create folder
	createFolder = (dto: FolderCreateDto) =>
		this.http.post<APIResponse<FolderViewDto>>(this.ROOT_URL, dto);

	// edit folder
	editFolder = (uuid: string, dto: FolderEditDto) =>
		this.http.patch<APIResponse<FolderViewDto>>(
			`${this.ROOT_URL}/${uuid}`,
			dto
		);

	// Invite into folder
	inviteInFolder = (uuid: string, dto: FolderInviteDto) =>
		this.http.post<APIResponse<FolderViewDto>>(
			`${this.ROOT_URL}/${uuid}/share`,
			dto
		);

	getMembers = (uuid: string) =>
		this.http.get<APIResponse<FolderMemberViewDto[]>>(
			`${this.ROOT_URL}/${uuid}/members`
		);

	editMemberRole = (folderUuid?: string, dto?: FolderMemberEditDto) =>
		this.http.patch<APIResponse<void>>(
			`${this.ROOT_URL}/${folderUuid}/members`,
			dto
		);

	removeMember = (folderUuid?: string, user_uuid?: string) =>
		this.http.delete<APIResponse<FolderMemberViewDto[]>>(
			`${this.ROOT_URL}/${folderUuid}/members`,
			{body: {user_uuid}}
		);

	// delete folder
	delete = (uuid: string) => this.http.delete(`${this.ROOT_URL}/${uuid}`);

	currentUserIsEditorInAtLeastOneFolder = (uuid?: string) =>
		uuid
			? this.getFoldersFlat(uuid).pipe(
					map(folders => folders.some(f => f.folderRole === 'editor'))
			  )
			: of(false);

	private sortFolders(a: FolderViewDto, b: FolderViewDto): number {
		if (a.level !== b.level) return a.level - b.level;
		return a.name.localeCompare(b.name);
	}

	trash = (folderUuid: string) =>
		this.http.patch(`${this.ROOT_URL}/${folderUuid}/trash`, null);

	restore = (folderUuid: string) =>
		this.http.patch(`${this.ROOT_URL}/${folderUuid}/restore`, null);

	download = (folderUuid: string) =>
		this.http.get(`${this.ROOT_URL}/${folderUuid}/download`, {
			responseType: 'blob',
			reportProgress: true,
			observe: 'events',
		});

	exportReport = (folderUuid: string) =>
		this.http.get(`${this.ROOT_URL}/${folderUuid}/report/export`, {
			responseType: 'blob',
			observe: 'response',
		});

	getTree = (folder_uuid: string): Observable<FolderViewDto> =>
		this.http
			.get<APIResponse<any>>(`${this.ROOT_URL}/${folder_uuid}/tree`)
			.pipe(map(res => res?.data));

	getFolderDetails = (folder_uuid: string): Observable<FolderDetails> =>
		this.http
			.get<APIResponse<FolderDetails>>(
				`${this.ROOT_URL}/${folder_uuid}/details`
			)
			.pipe(map(res => res?.data));

	archive = (folderUuid: string) =>
		this.http
			.patch<APIResponse<FolderViewDto>>(
				`${this.ROOT_URL}/${folderUuid}/archive`,
				null
			)
			.pipe(map(res => res?.data));

	unarchive = (folderUuid: string) =>
		this.http
			.patch<APIResponse<FolderViewDto>>(
				`${this.ROOT_URL}/${folderUuid}/unarchive`,
				null
			)
			.pipe(map(res => res?.data));

	createBreadCrumbObj(folder: FolderViewDto) {
		const breadCrumbMenu: {uuid: string; name: string; level: number}[] = [];
		function extract(folder: FolderViewDto) {
			breadCrumbMenu.push({
				uuid: folder.uuid,
				name: folder.name,
				level: folder.level,
			});
			folder?.subFolders?.forEach(extract);
		}
		extract(folder);

		return breadCrumbMenu;
	}

	extractFolderByUuid(
		arr: FolderViewDto[],
		targetUuid?: string | null
	): Observable<FolderViewDto | null> {
		if (!targetUuid) {
			return of(null); // TODO check if this is ok
		}
		const result = this.findFolderRecursive(arr, targetUuid);
		return of(result);
	}

	private findFolderRecursive(
		folderArray: FolderViewDto[],
		targetUuid: string
	): FolderViewDto | null {
		for (const item of folderArray) {
			if (item.uuid === targetUuid) {
				return item;
			} else if (item.subFolders?.length) {
				const foundFolder = this.findFolderRecursive(
					item.subFolders,
					targetUuid
				);
				if (foundFolder) {
					return foundFolder;
				}
			}
		}
		return null;
	}

	extractSubChild(arr: FolderViewDto[], targetUuid: string): FolderViewDto[] {
		for (const item of arr) {
			if (item.uuid === targetUuid) {
				return item.subFolders;
			} else if (item.subFolders) {
				const child = this.extractSubChild(item.subFolders, targetUuid);
				if (child.length > 0) {
					return child;
				}
			}
		}
		return [];
	}

	getChild(arr: FolderViewDto[], targetUuid: string): FolderViewDto[] {
		for (const item of arr) {
			if (item.uuid === targetUuid) {
				return [{...item, subFolders: []}];
			} else if (item.subFolders?.length) {
				const child = this.getChild(item.subFolders, targetUuid);
				if (child.length) {
					return [{...item, subFolders: child}];
				}
			}
		}
		return [];
	}

	extractChild(
		arr: FolderViewDto[],
		targetUuid: string
	): Observable<FolderViewDto[]> {
		const result = this.extractChildRecursive(arr, targetUuid);
		return of(result);
	}

	private extractChildRecursive(
		arr: FolderViewDto[],
		targetUuid: string
	): FolderViewDto[] {
		for (const item of arr) {
			if (item.uuid === targetUuid) {
				return [{...item, subFolders: []}];
			} else if (item.subFolders) {
				const child = this.extractChildRecursive(item.subFolders, targetUuid);
				if (child.length) {
					return [{...item, subFolders: child}];
				}
			}
		}
		return [];
	}

	updateFolders(
		folders: FolderViewDto[],
		targetUuid: string,
		updatedItems: FolderViewDto[]
	): Observable<FolderViewDto[]> {
		if (!targetUuid) return of([...folders, ...updatedItems]);
		return of(
			folders.map(folder =>
				this.updateSubFolder(folder, targetUuid, updatedItems)
			)
		);
	}

	updateSubFolder(
		folder: FolderViewDto,
		targetUuid: string,
		updatedItems: FolderViewDto[]
	): FolderViewDto {
		if (folder.uuid === targetUuid) {
			return {
				...folder,
				subFolders: [...(folder.subFolders || []), ...updatedItems],
			};
		}

		if (folder.subFolders?.length) {
			return {
				...folder,
				subFolders: folder.subFolders.map(subFolder =>
					this.updateSubFolder(subFolder, targetUuid, updatedItems)
				),
			};
		}

		return folder;
	}

	setCurrentFolder = (
		currentFolder: FolderViewDto | null,
		section?: 'DRIVE' | 'TRASH' | 'ARCHIVE'
	) => {
		sessionStorage.setItem(
			'navigation-context',
			JSON.stringify({
				currentFolder: currentFolder,
				section: section,
			})
		);
		this.store.dispatch(
			FolderActions.setCurrentFolder({
				folder: currentFolder,
				section: section,
			})
		);
	};
}
